import React, { useCallback, useMemo, useRef, useState } from 'react';
import { TypedMemo } from '../../common/types';
import { useMusicVideoEditor } from '../MusicVideoEditorContext';
import { docRange } from '../doc/clipOps';
import { EffectsLane } from './EffectsLane';
import { FootageLane } from './FootageLane';
import { LyricsLane } from './LyricsLane';
import { RecutBox } from './RecutBox';
import { RulerLane } from './RulerLane';
import { SectionsLane } from './SectionsLane';
import { SongLane } from './SongLane';
import { TextLane } from './TextLane';
import { TimelineBar } from './TimelineBar';
import { TimelineGuide, type DragGuide } from './TimelineGuide';
import { TimelineHeads } from './TimelineHeads';
import { TimelineScroll } from './TimelineScroll';
import { isWholeView, secToPercent, showsEdge } from './timelineGeometry';
import { useTimelineView } from './useTimelineView';
import styles from './Timeline.module.scss';

/** What the lanes need from the timeline: the view, its scale, the snap switch and the drag guide. */
export interface LaneProps {
  view: { from: number; to: number };
  /** Pixels of the lane for one second of the song at this zoom. */
  pxPerSec: number;
  snapOn: boolean;
  onGuide: (guide: DragGuide | null) => void;
}

/**
 * The timeline (design music-video Timeline, c5, c6): the ruler, the sections, the footage, the
 * texts, the effects, the lyrics (read only) and the song (beats and energy; locked), with the play head.
 * "Whole song" shows the video; "Section" fits the selected section; + and − (also Ctrl/⌘ with the
 * wheel, a pinch, the + and − keys) zoom in and out, and a zoomed view scrolls. Snap to the beat is
 * on by default; Alt holds it off during a drag.
 */
const TimelineComponent: React.FC<{ recutTarget: number | 'all' | null; onCloseRecut: () => void }> = ({
  recutTarget,
  onCloseRecut,
}) => {
  const { doc, songMap, sectionIndex, transport } = useMusicVideoEditor();
  const [snapOn, setSnapOn] = useState(true);
  const [guide, setGuide] = useState<DragGuide | null>(null);
  const lanesRef = useRef<HTMLDivElement>(null);
  const section = useMemo(
    () => songMap.sections.find((item) => item.index === sectionIndex) ?? null,
    [sectionIndex, songMap.sections]
  );
  const range = docRange(doc);
  const timeline = useTimelineView({
    lanesRef,
    range,
    section,
    positionSec: transport.positionSec,
    isPlaying: transport.isPlaying,
  });
  const { view, pxPerSec } = timeline;
  // Some cuts in view are too close to grab: the bar says to zoom in.
  const cutsHidden = useMemo(
    () =>
      pxPerSec > 0 &&
      doc.clips.some((clip, index) => {
        const next = doc.clips[index + 1];
        return (
          next !== undefined && clip.endSec > view.from && clip.endSec < view.to && !showsEdge(clip, next, pxPerSec)
        );
      }),
    [doc.clips, pxPerSec, view]
  );
  const onGuide = useCallback((next: DragGuide | null) => setGuide(next), []);
  const playLeft = secToPercent(transport.positionSec, view);

  return (
    <section className={styles.timeline}>
      <TimelineBar
        zoom={timeline.zoom}
        onZoom={timeline.setZoom}
        hasSection={section !== null}
        onZoomIn={timeline.zoomIn}
        onZoomOut={timeline.zoomOut}
        canZoomIn={timeline.canZoomIn}
        canZoomOut={timeline.canZoomOut}
        cutsHidden={cutsHidden}
        snapOn={snapOn}
        onSnap={() => setSnapOn((value) => !value)}
      />
      <div className={styles.grid}>
        <TimelineHeads />
        <div ref={lanesRef} id="music-video-timeline-lanes" className={styles.lanes}>
          <RulerLane view={view} pxPerSec={pxPerSec} snapOn={snapOn} onGuide={onGuide} />
          <SectionsLane view={view} pxPerSec={pxPerSec} snapOn={snapOn} onGuide={onGuide} />
          <FootageLane view={view} pxPerSec={pxPerSec} snapOn={snapOn} onGuide={onGuide} />
          <TextLane view={view} pxPerSec={pxPerSec} snapOn={snapOn} onGuide={onGuide} />
          <EffectsLane view={view} pxPerSec={pxPerSec} snapOn={snapOn} onGuide={onGuide} />
          <LyricsLane view={view} pxPerSec={pxPerSec} snapOn={snapOn} onGuide={onGuide} />
          <SongLane view={view} pxPerSec={pxPerSec} snapOn={snapOn} onGuide={onGuide} />
          {playLeft >= 0 && playLeft <= 100 && <div className={styles.playHead} style={{ left: `${playLeft}%` }} />}
          {guide !== null && <TimelineGuide guide={guide} view={view} />}
          {!isWholeView(view, range) && <TimelineScroll view={view} range={range} onScroll={timeline.scrollTo} />}
        </div>
      </div>
      {recutTarget !== null && <RecutBox target={recutTarget} onClose={onCloseRecut} />}
    </section>
  );
};

export const Timeline = TypedMemo(TimelineComponent);
